/**
 * Arabic copy for the ops Complaints page «الشكاوى» and the «فتح شكوى» form (tasks/complaints-to-fixes.md).
 *
 * Ops queue pages are Arabic-only (I18N_STRATEGY — only the crew's task screens are bilingual), so the words
 * live here, the way guestReviewLabels.js serves the Reviews page. The API sends CODES; this file is where they
 * become words. The code lists MUST match be/config/complaints.js. Task type and status words come from the
 * locale file the task screens use, so the two can never drift.
 */
import ar from '../locales/ar';
import { MANUAL_TASK_TYPES } from './taskTypes';

// C8 — must match be/config/complaints.js CATEGORIES.
export const CATEGORIES = ['access', 'cleanliness', 'equipment', 'maintenance', 'noise', 'property_condition', 'housekeeping', 'other'];
export const CATEGORY_LABELS = {
  access: 'الدخول والأقفال',
  cleanliness: 'النظافة',
  equipment: 'معدات أو أغراض ناقصة',
  maintenance: 'أعطال وصيانة',
  noise: 'الإزعاج',
  property_condition: 'حالة الوحدة',
  housekeeping: 'مستلزمات الضيافة',
  other: 'أخرى'
};

// C7
export const SEVERITIES = ['high', 'medium', 'low'];
export const SEVERITY_LABELS = { high: 'عالية', medium: 'متوسطة', low: 'منخفضة' };

// C10 — who it waited on: the history's words (off the screen since the three-step card).
export const WAITING_ON_LABELS = {
  ops: 'فريق العمليات',
  maintenance: 'الصيانة',
  housekeeping: 'فريق النظافة',
  owner: 'المالك',
  supplier: 'المورّد'
};

// C11 — the named change: the history's words (off the screen since the three-step card).
export const PREVENTION_CHANGE_LABELS = {
  checklist: 'تعديل قائمة التنظيف',
  repair_replace: 'إصلاح أو استبدال',
  supplier: 'تغيير المورّد',
  listing: 'تحديث الإعلان',
  inspection: 'تغيير روتين الفحص',
  other: 'أخرى'
};

export const GUEST_ISSUE_LABELS = {
  open: 'لم تُعالج بعد',
  handled: 'تمت المعالجة',
  not_applicable: 'لا ينطبق'
};

export const PREVENTION_STATUS_LABELS = {
  open: 'لم يُحدَّد بعد',
  planned: 'محدد — لم يُنفَّذ',
  done: 'نُفِّذ',
  not_needed: 'لا حاجة'
};

// C15 — the lists, in tab order, decided by repair tasks alone (tasks/followup-tasks-only.md F5). The server
// puts each complaint in its list; the first is the default and the menu counter.
export const LISTS = ['needs_action', 'in_progress', 'closed'];
export const LIST_LABELS = {
  needs_action: 'بحاجة لإجراء',
  in_progress: 'قيد التنفيذ',
  closed: 'أُغلقت مؤخراً'
};
export const LIST_EMPTY = {
  needs_action: 'لا توجد شكاوى بحاجة لإجراء.',
  in_progress: 'لا توجد شكاوى لها مهمة مفتوحة.',
  closed: 'لم تُغلق أي شكوى مؤخراً.'
};

export const SOURCE_LABELS = {
  message: 'من رسائل الضيف',
  review: 'من تقييم الضيف',
  ota_review: 'من تقييم الضيف',
  booking: 'من صفحة الحجز'
};

// C13 / C20 — a complaint raises the same three types as anywhere else (be/config/tasks.js).
export const REPAIR_TYPES = MANUAL_TASK_TYPES;
// Wider than the list above: "تدبير منزلي" is no longer offered, but tasks raised before that
// decision still have to read correctly wherever they are shown.
export const REPAIR_TYPE_LABELS = {
  cleaning: ar.tasks.type.cleaning,
  maintenance: ar.tasks.type.maintenance,
  restock: ar.tasks.type.restock,
  housekeeping: ar.tasks.type.housekeeping
};
export const TASK_STATUS_LABELS = {
  pending: ar.tasks.status.pending,
  assigned: ar.tasks.status.assigned,
  'in-progress': ar.tasks.status.inProgress,
  completed: ar.tasks.status.completed,
  cancelled: ar.tasks.status.cancelled
};

// C12 — what still blocks «إغلاق» (codes from the server's closeBlockers): only an open task since the
// three-step card — closing records the guest issue and prevention itself (tasks/followup-tasks-only.md B4/B5).
export const BLOCKER_TEXT = {
  open_tasks: 'إنهاء مهام الإصلاح المفتوحة'
};

// B4/B5 — how a complaint was closed (be/config/complaints.js CLOSE_OUTCOMES); also the history's words.
export const CLOSE_OUTCOME_LABELS = {
  no_task: 'لا تحتاج مهمة',
  change: 'تغيير يمنع التكرار',
  no_change: 'لا يلزم تغيير',
  // Closed by the system when its tasks were done (tasks/complaint-auto-close-on-tasks-done.md).
  tasks_done: 'أُغلقت تلقائياً — انتهت المهام'
};

// C17 — the history's actions (be/models/ComplaintEvent.js).
export const HISTORY_LABELS = {
  created: 'فُتحت الشكوى',
  source_added: 'أُضيف مصدر جديد',
  details_changed: 'عُدّلت التفاصيل',
  accountable_changed: 'تغيّر المسؤول',
  waiting_on_changed: 'تغيّر من ننتظره',
  guest_issue_changed: 'تحديث مشكلة الضيف',
  compensation_changed: 'تحديث التعويض',
  prevention_changed: 'تحديث الإجراء الوقائي',
  task_created: 'أُنشئت مهمة إصلاح',
  task_linked: 'أُضيفت إلى مهمة مفتوحة',
  closed: 'أُغلقت الشكوى',
  reopened: 'أُعيد فتح الشكوى',
  chased: 'متابعة'
};

export const TEXT = {
  // The tab on «التقييمات والشكاوى» (C19) and its four list buttons.
  tabTitle: 'الشكاوى',
  viewsLabel: 'قوائم الشكاوى',
  refresh: 'تحديث',
  loading: 'جارٍ تحميل الشكاوى…',
  loadError: 'تعذّر تحميل الشكاوى. حاول مرة أخرى.',
  emptyHint: 'تُفتح الشكوى من محادثة الضيف في صندوق الرسائل، أو من تقييم يحتاج انتباه، أو من صفحة الحجز.',

  // The three steps (tasks/followup-tasks-only.md Part B): what happened → a task or «لا تحتاج مهمة» → close
  loopLabel: 'خطوات الشكوى',
  stepWhat: 'ما حدث',
  stepAction: 'الإجراء',
  stepClose: 'الإغلاق',
  actionNeeded: 'بحاجة لإجراء',
  actionTaskOpen: 'مهمة مفتوحة',
  actionTasksDone: 'انتهت المهام',
  closeAfterAction: 'بعد الإجراء',
  closeLater: 'بعد انتهاء المهام',
  until: 'حتى',
  overdueSince: 'متأخرة منذ',
  readyToClose: 'جاهزة للإغلاق',
  closedOn: 'أُغلقت',
  late: 'متأخرة',
  booking: 'الحجز',
  showDetails: 'فتح التفاصيل',
  hideDetails: 'إخفاء التفاصيل',

  // Detail
  sourcesTitle: 'ما قاله الضيف',   // the «فتح شكوى» form
  noExcerpt: 'فُتحت من صفحة الحجز — لا يوجد نص من الضيف.',
  actionHint: 'أنشئ مهمة إصلاح لها مسؤول وموعد، أو سجّل أنها لا تحتاج مهمة.',
  noTasks: 'لا توجد مهام إصلاح.',
  addTask: 'إنشاء مهمة',
  addAnotherTask: 'إضافة مهمة أخرى',
  noTaskNeeded: 'لا تحتاج مهمة',
  noTaskPlaceholder: 'لماذا لا تحتاج مهمة؟ مثال: أُصلح العطل أثناء الإقامة',
  noTaskLabel: 'سبب عدم الحاجة لمهمة',
  closeAfterActionHint: 'تُغلق بإنشاء مهمة ثم إنهائها، أو مباشرة بـ«لا تحتاج مهمة».',
  closeAfterTasks: 'تُغلق بعد انتهاء مهامها.',
  closeHint: 'انتهت المهام. سطر واحد يكفي للإغلاق.',
  closeChooseFirst: 'اختر أولاً: تغيير يمنع التكرار، أو لا يلزم تغيير.',
  closePlaceholder: 'ما الذي تغيّر حتى لا تتكرر؟ أو لماذا لا يلزم تغيير؟',
  closeLineLabel: 'سطر الإغلاق',
  reopenLabel: 'سبب إعادة الفتح',
  taskType: 'نوع المهمة',
  // C20-2 — ops choose; nothing is read off the category. One line, because the boundary is the
  // whole point: a consumable is refilled, a visit is cleaning, an item is bought or replaced.
  taskTypeHint: 'نظافة: زيارة فريق النظافة (إعادة تنظيف، مناشف، مفارش) · صيانة: إصلاح أو شراء أو استبدال · مستلزمات ضيافة: مستهلكات الضيف في قائمة المستلزمات.',
  taskSupplies: 'المستلزمات المطلوبة',
  taskSuppliesHint: 'إن كانت للوحدة مهمة مستلزمات مفتوحة، ستُضاف إليها — ويمكن تقديم موعدها أو رفع أولويتها فقط.',
  taskJoined: 'أُضيفت إلى مهمة المستلزمات المفتوحة',
  taskTitle: 'عنوان المهمة',
  taskTitlePlaceholder: 'عنوان المهمة — مثال: استبدال رأس صنبور المطبخ',
  taskNotesPlaceholder: 'ملاحظات للفريق (اختياري) — بكلماتك، لا تنسخ كلام الضيف',
  taskAssignee: 'تكليف (اختياري)',
  unassigned: 'بدون تكليف',
  taskDate: 'موعد التنفيذ',
  createTask: 'إنشاء المهمة',
  // The history still shows compensation recorded before the three-step card.
  noCompensation: 'لم يُسجَّل تعويض.',
  night: 'ليلة',
  twoNights: 'ليلتان',
  nights: 'ليالٍ',
  movedNote: 'نُقل الضيف إلى وحدة أخرى خلال هذه الإقامة.',
  editDetails: 'تعديل التفاصيل',
  category: 'نوع المشكلة',
  severity: 'الأهمية',
  severityHint: 'عالية: نُقل الضيف، أو تعذّر دخوله، أو مشكلة سلامة أو صحة، أو تعويض كبير.',
  suggestedHint: 'التصنيف ودرجة الأهمية مقترحان آلياً من التقييم — راجعهما وغيّرهما عند الحاجة.',
  descriptionPlaceholder: 'ما المشكلة؟ بكلماتك.',
  close: 'إغلاق الشكوى',
  closeBlocked: 'لا تُغلق قبل:',
  reopen: 'إعادة فتح الشكوى',
  reopenPlaceholder: 'لماذا تُعاد فتحها؟',
  historyTitle: 'السجل',
  historyError: 'تعذّر تحميل السجل.',
  from: 'من',
  to: 'إلى',
  descriptionChanged: 'عُدّل الوصف',
  notListed: 'هذه الشكوى ليست في القوائم — ربما أُغلقت منذ مدة.',
  openNow: 'الشكوى المفتوحة',
  agent: 'المتابعة الآلية',
  system: 'النظام',
  edit: 'تعديل',
  save: 'حفظ',
  cancel: 'إلغاء',
  confirm: 'تأكيد',

  // The «فتح شكوى» form
  openComplaint: 'فتح شكوى',
  modalTitle: 'فتح شكوى',
  cancelledBooking: 'الحجز ملغى — يمكن تسجيل الشكوى مع ذلك.',
  linked: 'هذا المصدر مضاف بالفعل إلى شكوى مفتوحة. إن كانت مشكلة أخرى، افتح لها شكوى جديدة.',
  alreadyHere: 'مضاف إليها',
  view: 'عرض',
  existingTitle: 'شكاوى مفتوحة لهذه الإقامة',
  existingHint: 'إن كانت المشكلة نفسها، أضف هذا المصدر إليها بدل فتح شكوى جديدة.',
  attach: 'إضافة إليها',
  newComplaint: 'شكوى جديدة',
  unitChoice: 'الوحدة التي حدثت فيها المشكلة',
  guestMoved: 'نُقل هذا الضيف بين أكثر من وحدة — اختر الوحدة الصحيحة.',
  departedHint: 'غادر الضيف — يبقى المطلوب مهمة إصلاح، أو تسجيل أنها لا تحتاج مهمة.',
  submit: 'فتح الشكوى',
  created: 'فُتحت الشكوى.',
  attached: 'أُضيف المصدر إلى الشكوى.',
  viewComplaint: 'عرض الشكوى',
  unexpected: 'حدث خطأ غير متوقع'
};

// Server refusals (be/utils/errors.js COMPLAINT_xxx) — "must match backend".
// COMPLAINT_001 — `details.what` says what is missing: the complaint (none), the booking or the source.
const NOT_FOUND_TEXT = {
  booking: 'الحجز غير موجود.',
  source: 'المحادثة أو التقييم غير موجود — حدّث الصفحة.'
};
const ERROR_TEXT = {
  COMPLAINT_001: 'الشكوى غير موجودة — حدّث الصفحة.',
  COMPLAINT_002: 'لا يمكن تنفيذ هذا الإجراء الآن — ربما أُغلقت الشكوى أو تغيّرت. حدّث الصفحة.',
  COMPLAINT_004: 'لا يوجد حجز مرتبط بهذه المحادثة أو التقييم — لا يمكن فتح شكوى من هنا.',
  COMPLAINT_005: 'هذه الوحدة ليست من وحدات إقامة الضيف.',
  COMPLAINT_006: 'وصلت الشكوى إلى الحد الأقصى — لا يمكن إضافة المزيد.',
  SYS_001: 'حدث خطأ غير متوقع — حاول مرة أخرى.'
};

// COMPLAINT_003 — `details.field` says which.
const INVALID_FIELD_TEXT = {
  description: 'اكتب وصف المشكلة.',
  category: 'اختر نوع المشكلة.',
  severity: 'اختر الأهمية.',
  note: 'اكتب ماذا فعلنا للضيف — أو لماذا لا ينطبق.',
  change: 'اختر ما الذي سيتغير.',
  text: 'اكتب التغيير المحدد أو السبب.',
  title: 'اكتب عنوان المهمة.',
  type: 'اختر نوع المهمة.',
  assignedTo: 'لا يمكن تكليف هذا الشخص بالمهمة.',
  accountable: 'لا يمكن جعل هذا الشخص مسؤولاً عن الشكوى.',
  freeNights: 'عدد الليالي يجب أن يكون رقماً صحيحاً.',
  amount: 'المبلغ غير صحيح.',
  reason: 'اكتب سبب إعادة الفتح.',
  source: 'هذا المصدر من حجز آخر — لا يُضاف إلى هذه الشكوى.',
  scheduledDate: 'اختر تاريخاً صحيحاً من اليوم فصاعداً.',
  waitingOn: 'اختر من ننتظره.',
  status: 'اختر حالة صحيحة.',
  notes: 'الملاحظات طويلة — اختصرها.',
  restockItems: 'اختر مستلزماً واحداً على الأقل من القائمة.',
  changes: 'لا يوجد تعديل للحفظ.',
  outcome: 'اختر طريقة الإغلاق.',
  closeNote: 'اكتب سطراً واحداً قبل الإغلاق.'
};

/** A refused action, in words — from the code when there is one (I18N_STRATEGY). */
export const errorText = (err) => {
  const data = err?.response?.data;
  if (data?.code === 'COMPLAINT_001' && NOT_FOUND_TEXT[data?.details?.what]) return NOT_FOUND_TEXT[data.details.what];
  if (data?.code === 'COMPLAINT_003') return INVALID_FIELD_TEXT[data?.details?.field] || 'بيانات غير مكتملة.';
  if (data?.code === 'COMPLAINT_007') {
    const missing = (data?.details?.missing || []).map(code => BLOCKER_TEXT[code] || code);
    return `${TEXT.closeBlocked} ${missing.join('، ')}`;
  }
  if (data?.code && ERROR_TEXT[data.code]) return ERROR_TEXT[data.code];
  return data?.error || TEXT.unexpected;
};
